import { useQueries, useInfiniteQuery } from '@tanstack/react-query';
import { getOrdersListV2, getOrdersHeaderSummaryV2 } from '../../services/order.service';
import { getStoreByIdV2, getCashierBoxesV2 } from '../../services/store.service';
import { IOrder } from '../../interfaces/Order';

interface UseOrdersByStatusQueriesParams {
  storeId: string;
  searchTerm?: string;
  orderStatusFilter: string;
  showTableView: boolean;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  limitPerReq: number;
  assignedEmployee?: string;
}

interface IQueryFnParams {
  orders: IOrder[];
  totalRows: [{ totalRowsCount: number }];
}

interface UseOrdersByStatusQueriesResult {
  store: {
    data: any;
    isLoading: boolean;
    error: any;
  };
  cashierboxes: {
    data: any;
    isLoading: boolean;
    error: any;
  };
  orders: {
    data: any;
    isLoading: boolean;
    error: any;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    refetch: () => void;
  };
  summary: {
    data: any;
    isLoading: boolean;
    error: any;
    refetch: () => void;
    isFetching: boolean;
  };
  isAnyLoading: boolean;
}

export const useOrdersByStatusQueries = ({
  storeId,
  searchTerm,
  orderStatusFilter,
  showTableView,
  pagination,
  limitPerReq,
  assignedEmployee
}: UseOrdersByStatusQueriesParams): UseOrdersByStatusQueriesResult => {
  // Parallel queries for store, cashierboxes, and summary
  const [storeQuery, cashierboxesQuery, summaryQuery] = useQueries({
    queries: [
      // Store query
      {
        queryKey: ['store', { _id: storeId }],
        enabled: !!storeId,
        queryFn: async () => {
          try {
            const res = await getStoreByIdV2(storeId);
            if (!res) {
              throw new Error('Oh no! Something went wrong while fetching store.');
            }
            return res;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      },
      // Cashierboxes query
      {
        queryKey: ['cashierbox', { storeId }],
        enabled: !!storeId,
        queryFn: async () => {
          try {
            const res = await getCashierBoxesV2(storeId);
            if (!res?.data?.data?.cashierBoxes) {
              throw new Error('Oh no! Something went wrong while fetching cashierboxes.');
            }
            return res.data.data.cashierBoxes;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      },
      // Orders summary query
      {
        queryKey: ['ordersHeaderSummary', {
          status: orderStatusFilter,
          store: storeId
        }],
        enabled: !!storeId,
        queryFn: async ({ queryKey }) => {
          try {
            const [_key, params] = queryKey;
            const res = await getOrdersHeaderSummaryV2(params as any);
            if (!res?.data?.data) {
              throw new Error('Oh no! Something went wrong while fetching orders summary.');
            }
            return res.data.data;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      }
    ]
  });

  // Separate infinite query for orders
  const ordersQuery = useInfiniteQuery<IQueryFnParams, Error, any, any, any>({
    queryKey: ['orders', {
      pageNum: showTableView ? pagination.pageIndex : undefined,
      q: searchTerm,
      status: orderStatusFilter,
      storeId,
      limit: limitPerReq,
      fetchSection: 'true',
      assignedEmployee
    }],
    enabled: !!storeId,
    initialPageParam: 0,
    getNextPageParam: (lastPage: IQueryFnParams, allPages: IQueryFnParams[], lastPageParam: number) => {
      const currentRows = allPages.reduce((prev, curr) => prev + curr.orders.length, 0) || 0;
      const lastPageValue = (lastPage.totalRows && lastPage.totalRows[0]) || { totalRowsCount: 0 };
      if (lastPageValue.totalRowsCount <= currentRows) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    getPreviousPageParam: (firstPage: IQueryFnParams, allPages: IQueryFnParams[], firstPageParam: number) => {
      if (firstPageParam <= 1) {
        return undefined;
      }
      return firstPageParam - 1;
    },
    queryFn: async ({ queryKey, pageParam }) => {
      try {
        const [_key, params] = queryKey as [string, any];
        const res = await getOrdersListV2({
          ...params,
          pageNum: params.pageNum || pageParam,
          limit: params.limit,
        });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching orders.');
        }
        return res.data.data as IQueryFnParams;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    }
  });

  const isAnyLoading = storeQuery.isLoading || cashierboxesQuery.isLoading || summaryQuery.isLoading || ordersQuery.isLoading;

  return {
    store: {
      data: storeQuery.data,
      isLoading: storeQuery.isLoading,
      error: storeQuery.error
    },
    cashierboxes: {
      data: cashierboxesQuery.data,
      isLoading: cashierboxesQuery.isLoading,
      error: cashierboxesQuery.error
    },
    orders: {
      data: ordersQuery.data,
      isLoading: ordersQuery.isLoading,
      error: ordersQuery.error,
      fetchNextPage: ordersQuery.fetchNextPage,
      hasNextPage: ordersQuery.hasNextPage || false,
      refetch: ordersQuery.refetch
    },
    summary: {
      data: summaryQuery.data,
      isLoading: summaryQuery.isLoading,
      error: summaryQuery.error,
      refetch: summaryQuery.refetch,
      isFetching: summaryQuery.isFetching
    },
    isAnyLoading
  };
};
